import React, { useEffect, useRef, useState } from "react";
import PuceStatut from "./PuceStatut";
import {
  estDefinitif,
  formaterDate,
  presentationStatut,
  progression,
  resultatCible,
  traitementBloque,
} from "./effacementDonnees";

const phrase = (texte) => (/[.!?…]$/.test(texte.trim()) ? texte.trim() : `${texte.trim()}.`);

const MESSAGES = {
  terminee: () => ({
    ton: "succes",
    icone: "check-circle",
    titre: "Toutes les données de cette personne ont été effacées.",
    textes: ["Son adresse n'est plus conservée : seule cette trace reste au registre."],
  }),
  refusee: (demande) => ({
    ton: "alerte",
    icone: "ban",
    titre: "Effacement refusé, rien n'a été effacé.",
    textes: [demande.derniereErreur],
  }),
  abandonnee: () => ({
    ton: "neutre",
    icone: "minus-circle",
    titre: "Effacement abandonné.",
    textes: ["L'adresse a été oubliée : une nouvelle recherche permet de reprendre."],
  }),
  "en-echec": (demande) => ({
    ton: "echec",
    icone: "exclamation-triangle",
    titre: "L'effacement s'est arrêté.",
    textes: [
      phrase(demande.derniereErreur || "Une étape a échoué"),
      "Relancez quand la cause est réglée : toutes les étapes sont rejouées, sans risque.",
    ],
  }),
  "en-cours": () => ({
    ton: "encours",
    icone: "sync-alt fa-spin",
    titre: "Effacement en cours.",
    textes: ["Le détail se met à jour tout seul."],
  }),
  "en-attente": () => ({
    ton: "encours",
    icone: "clock",
    titre: "Effacement en attente de traitement.",
    textes: ["Le détail se met à jour tout seul."],
  }),
};

const messageDuStatut = (demande) =>
  MESSAGES[demande.statut.id]?.(demande) || {
    ton: "neutre",
    icone: "info-circle",
    titre: `Statut : ${demande.statut.libelle}.`,
    textes: [],
  };

const focaliserLeTitre = () => document.getElementById(TITRE_SUIVI)?.focus();

const etapes = (nombre) => `${nombre} étape${nombre > 1 ? "s" : ""}`;

export const TITRE_SUIVI = "titre-suivi";

const SuiviEffacement = ({ demande, onRelancer, onAbandonner, onFermer }) => {
  const [abandonDemande, setAbandonDemande] = useState(false);
  const boutonAbandonner = useRef(null);
  const boutonAnnuler = useRef(null);
  const confirmationVue = useRef(false);
  const statut = demande.statut.id;
  const message = messageDuStatut(demande);
  const { faites, total } = progression(demande);
  const indexCourant = statut === "en-cours" ? demande.cibles.findIndex((c) => c.statut.id !== "faite") : -1;
  const actionsVisibles = !estDefinitif(statut) && (statut === "en-echec" || traitementBloque(demande));

  useEffect(() => {
    setAbandonDemande(false);
  }, [statut, demande.id]);

  // Le bouton cliqué disparaît : le focus passe au choix sûr, sinon il retomberait sur la page.
  useEffect(() => {
    if (abandonDemande) boutonAnnuler.current?.focus();
    else if (confirmationVue.current) boutonAbandonner.current?.focus();
    confirmationVue.current = abandonDemande;
  }, [abandonDemande]);

  const relancer = () => {
    focaliserLeTitre();
    onRelancer(demande.id);
  };

  const abandonner = () => {
    focaliserLeTitre();
    onAbandonner(demande.id);
  };

  return (
    <>
      <header className="effacement__panneau-tete">
        <div className="effacement__panneau-barre">
          <div>
            <h2 className="effacement__titre" id={TITRE_SUIVI} tabIndex={-1}>
              Effacement du {formaterDate(demande.demandeeLe)}
            </h2>
            <p className="effacement__meta">
              Demandé par {demande.demandeePar.nomComplet || "un utilisateur supprimé"}
              {demande.tentative > 1 && ` · tentative ${demande.tentative}`}
              {demande.termineeLe && ` · clos le ${formaterDate(demande.termineeLe)}`}
            </p>
          </div>
          <button
            type="button"
            className="effacement__bouton-icone"
            onClick={onFermer}
            aria-label="Fermer le détail (Échap)"
            title="Fermer (Échap)"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="effacement__panneau-etat">
          <PuceStatut statut={demande.statut} />
          <span className="effacement__progression-texte">
            {etapes(faites)} sur {total}
          </span>
        </div>
        <div
          className="effacement__progression"
          role="progressbar"
          aria-label="Avancement de l'effacement"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={faites}
        >
          {demande.cibles.map((cible, index) => {
            const ton = index === indexCourant ? "encours" : presentationStatut(cible.statut.id).ton;
            return <span key={cible.cible.id} className={`effacement__ton--${ton}`} />;
          })}
        </div>
      </header>

      <div className="effacement__panneau-contenu">
        <div className={`effacement__message effacement__ton--${message.ton}`} role="status" aria-live="polite">
          <i className={`fa fa-${message.icone}`} aria-hidden="true" />
          <div>
            <strong>{message.titre}</strong>
            {message.textes.filter(Boolean).map((texte) => (
              <p key={texte}>{texte}</p>
            ))}
          </div>
        </div>

        <ol className="effacement__etapes">
          {demande.cibles.map((cible, index) => {
            const courante = index === indexCourant;
            const { ton, icone, prefixe } = courante
              ? { ton: "encours", icone: "spinner fa-spin", prefixe: "fa" }
              : presentationStatut(cible.statut.id);
            return (
              <li key={cible.cible.id} className={`effacement__etape effacement__ton--${ton}`}>
                <i className={`${prefixe} fa-${icone}`} aria-hidden="true" />
                <span className="effacement__etape-libelle">{cible.cible.libelle}</span>
                <span className="effacement__etape-resultat">
                  {courante ? "En cours…" : resultatCible(cible, statut)}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      {actionsVisibles && (
        <footer className="effacement__panneau-pied">
          {abandonDemande ? (
            <>
              <span className="effacement__question">L'adresse sera oubliée et l'effacement clos.</span>
              <button className="btn btn-danger" onClick={abandonner}>
                Confirmer l'abandon
              </button>
              <button className="btn btn-link" ref={boutonAnnuler} onClick={() => setAbandonDemande(false)}>
                Annuler
              </button>
            </>
          ) : (
            <>
              {statut !== "en-echec" && <span className="effacement__question">Le traitement semble bloqué ?</span>}
              <button className="btn btn-primary" onClick={relancer}>
                <i className="fa fa-redo mr-2" aria-hidden="true" />
                Relancer
              </button>
              <button className="btn btn-link text-danger" ref={boutonAbandonner} onClick={() => setAbandonDemande(true)}>
                Abandonner
              </button>
            </>
          )}
        </footer>
      )}
    </>
  );
};

export default SuiviEffacement;
